import { httpsCallable } from "firebase/functions";
import { functions } from "../config";
import type { TimelineConfig } from "../utils/timeline";
import type { MenuConfig } from "../utils/menu";

export interface FirestoreTimestamp {
  _seconds: number;
  _nanoseconds: number;
}

export interface SiteConfigResponse {
  siteId: string;
  name: string;
  slug: string;
  expiresAt: FirestoreTimestamp | null;
  archived: boolean;
  displayName: string | null;
  config: {
    language?: string;
    textOverrideId?: string;
    colorScheme?: {
      seeds?: Record<string, string>;
      palette: Record<string, string>;
    };
    fontScheme?: {
      heading: { family: string; category: string };
      body: { family: string; category: string };
      cssUrl: string;
    };
    permissions?: {
      allowGallery?: boolean;
      allowSingleImageDownload?: boolean;
      allowZipDownload?: boolean;
      allowTimeline?: boolean;
      allowMenu?: boolean;
      allowDjRequests?: boolean;
    };
    timeline?: TimelineConfig;
    menu?: MenuConfig;
    [key: string]: unknown;
  };
}

const getSiteConfigCallable = httpsCallable<
  { slug: string },
  SiteConfigResponse
>(functions, "getSiteConfig");

export async function getSiteConfig(slug: string): Promise<SiteConfigResponse> {
  const { data } = await getSiteConfigCallable({ slug });
  return data;
}

export interface SiteStatusResponse {
  name: string;
  archived: boolean;
  expiresAt: FirestoreTimestamp | null;
}

const getSiteStatusCallable = httpsCallable<
  { slug: string },
  SiteStatusResponse
>(functions, "getSiteStatus");

/**
 * Lightweight archive/expiry check used by baked PROD builds after the
 * snapshot has already painted, so takedowns/expirations apply without
 * a rebuild.
 */
export async function getSiteStatus(slug: string): Promise<SiteStatusResponse> {
  const { data } = await getSiteStatusCallable({ slug });
  return data;
}

/**
 * Convert the Firestore-style timestamp returned by callables into a JS Date.
 */
export function timestampToDate(ts: FirestoreTimestamp | null): Date | null {
  if (!ts) return null;
  return new Date(ts._seconds * 1000 + Math.floor(ts._nanoseconds / 1e6));
}

/**
 * Convert an ISO-8601 string (e.g. a baked snapshot's `expiresAt`) into the
 * Firestore-style timestamp shape used across the site store.
 */
export function isoToTimestamp(iso: string | null): FirestoreTimestamp | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  return { _seconds: Math.floor(ms / 1000), _nanoseconds: (ms % 1000) * 1e6 };
}

